import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  ApiError,
  formatBytes,
  formatDate,
  uploadToStorage,
  abreEnEditor,
  urlEditor,
  type FileEntry,
} from './api';
import { HistorialDialog } from './Historial';
import { CompartirDialog, LinkDialog, MoverDialog, RenombrarDialog } from './Dialogos';
import { IconoDe } from './Icono';
import {
  BarraSeleccion,
  Casilla,
  CasillaTodos,
  enLote,
  resumenLote,
  useSeleccion,
  type ResultadoLote,
} from './seleccion';
import { Confirmar, MenuAcciones, Pedir } from './ui';

type Subida = { nombre: string; progreso: number; error?: string };
type Vista = 'lista' | 'mosaico';
type Dialogo =
  | {
      tipo: 'renombrar' | 'link' | 'historial';
      entry: FileEntry;
    }
  // Mover, compartir y borrar sirven para uno o para la selección entera.
  | { tipo: 'mover' | 'compartir' | 'borrar'; entries: FileEntry[] }
  | { tipo: 'nueva-carpeta' }
  | null;

const nombreDe = (e: FileEntry) => e.name;

export function Explorer({
  path,
  onPathChange,
  onQuota,
}: {
  path: string;
  onPathChange: (p: string) => void;
  onQuota: (quota: { usedBytes: string; totalBytes: string }) => void;
}) {
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [siguiente, setSiguiente] = useState<string | null>(null);
  const [totalBytes, setTotalBytes] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [subidas, setSubidas] = useState<Subida[]>([]);
  const [arrastrandoArchivos, setArrastrandoArchivos] = useState(false);
  const [moviendo, setMoviendo] = useState<FileEntry[] | null>(null);
  const [sobreCarpeta, setSobreCarpeta] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [busqueda, setBusqueda] = useState('');
  const [vista, setVista] = useState<Vista>(() => {
    try {
      return (localStorage.getItem('cloud.vista') as Vista) ?? 'lista';
    } catch {
      return 'lista';
    }
  });
  const inputArchivo = useRef<HTMLInputElement>(null);
  const centinela = useRef<HTMLDivElement>(null);
  // Cada carga nueva invalida las respuestas de las anteriores: si se cambia de
  // carpeta con una página en vuelo, esa página no se mezcla con la nueva.
  const turno = useRef(0);
  const pidiendoMas = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem('cloud.vista', vista);
    } catch {
      // Sin almacenamiento, la vista vuelve a lista en la próxima carga.
    }
  }, [vista]);

  const cargar = useCallback(
    async (destino: string) => {
      const mio = ++turno.current;
      pidiendoMas.current = false;
      setCargando(true);
      setError(null);
      try {
        const data = await api.list(destino);
        if (mio !== turno.current) return;
        setEntries(data.entries);
        setSiguiente(data.siguiente);
        setTotalBytes(data.quota.totalBytes);
        onQuota(data.quota);
      } catch (err) {
        if (mio !== turno.current) return;
        setError(err instanceof ApiError ? err.message : 'No pudimos leer la carpeta.');
      } finally {
        if (mio === turno.current) setCargando(false);
      }
    },
    [onQuota],
  );

  const cargarMas = useCallback(async () => {
    if (!siguiente || pidiendoMas.current) return;
    pidiendoMas.current = true;
    setCargandoMas(true);
    const mio = turno.current;
    try {
      const data = await api.list(path, siguiente);
      if (mio !== turno.current) return;
      setEntries((previas) => {
        const vistas = new Set(previas.map((e) => e.path));
        return [...previas, ...data.entries.filter((e) => !vistas.has(e.path))];
      });
      setSiguiente(data.siguiente);
    } catch (err) {
      if (mio === turno.current) {
        setError(err instanceof ApiError ? err.message : 'No pudimos traer más archivos.');
      }
    } finally {
      if (mio === turno.current) {
        pidiendoMas.current = false;
        setCargandoMas(false);
      }
    }
  }, [path, siguiente]);

  useEffect(() => {
    void cargar(path);
    setBusqueda('');
  }, [path, cargar]);

  // Scroll progresivo: cuando el final de la lista se acerca a la pantalla, se
  // pide la página siguiente. Si la primera no llena la pantalla, el centinela
  // ya está visible y sigue pidiendo hasta llenarla.
  useEffect(() => {
    const el = centinela.current;
    if (!el || !siguiente) return;
    const observador = new IntersectionObserver(
      (vistas) => {
        if (vistas.some((v) => v.isIntersecting)) void cargarMas();
      },
      { rootMargin: '600px' },
    );
    observador.observe(el);
    return () => observador.disconnect();
  }, [siguiente, cargarMas, cargando, vista]);

  // Buscar filtra lo cargado; para no dar "no hay resultados" por algo que
  // todavía no llegó, con una búsqueda activa se trae el resto de la carpeta.
  useEffect(() => {
    if (busqueda.trim() && siguiente && !cargandoMas) void cargarMas();
  }, [busqueda, siguiente, cargandoMas, cargarMas]);

  // Carpetas primero. Cada página trae las suyas, así que se reordena lo
  // acumulado y no página por página.
  const ordenadas = useMemo(
    () => [...entries.filter((e) => e.kind === 'folder'), ...entries.filter((e) => e.kind === 'file')],
    [entries],
  );

  const entradas = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) return ordenadas;
    return ordenadas.filter((e) => e.name.toLowerCase().includes(termino));
  }, [ordenadas, busqueda]);

  const carpetas = useMemo(
    () => entries.filter((e) => e.kind === 'folder').map((e) => e.path),
    [entries],
  );

  const claves = useMemo(() => entradas.map((e) => e.path), [entradas]);
  const sel = useSeleccion(claves);
  const seleccionadas = useMemo(
    () => entradas.filter((e) => sel.seleccion.has(e.path)),
    [entradas, sel.seleccion],
  );

  const { limpiar } = sel;
  useEffect(() => {
    limpiar();
  }, [path, limpiar]);

  /** Saca de la vista lo que ya no está en esta carpeta, sin volver a la primera página. */
  const quitarDeLaVista = useCallback(
    (paths: string[]) => {
      const fuera = new Set(paths);
      setEntries((previas) => previas.filter((e) => !fuera.has(e.path)));
      void api
        .confirmUpload()
        .then(({ usedBytes }) => onQuota({ usedBytes, totalBytes }))
        .catch(() => undefined);
    },
    [onQuota, totalBytes],
  );

  function informar(
    r: ResultadoLote<FileEntry>,
    total: number,
    mensaje: (cantidad: string) => string,
  ) {
    const resumen = resumenLote(r, total, nombreDe, mensaje);
    setAviso(resumen.aviso);
    setError(resumen.error);
  }

  async function subir(archivos: FileList | File[]) {
    const lista = Array.from(archivos);
    if (lista.length === 0) return;

    setSubidas(lista.map((f) => ({ nombre: f.name, progreso: 0 })));
    let subidos = 0;

    for (const [indice, archivo] of lista.entries()) {
      try {
        const { url } = await api.uploadUrl(path, archivo);
        await uploadToStorage(url, archivo, (progreso) => {
          setSubidas((previas) => previas.map((s, i) => (i === indice ? { ...s, progreso } : s)));
        });
        subidos++;
      } catch (err) {
        const mensaje = err instanceof ApiError ? err.message : 'Falló la subida.';
        setSubidas((previas) => previas.map((s, i) => (i === indice ? { ...s, error: mensaje } : s)));
      }
    }

    // Refrescar el listado no depende de que el aviso de cuota llegue bien.
    await api.confirmUpload().catch(() => undefined);
    await cargar(path);
    setSubidas((previas) => previas.filter((s) => s.error));
    // En una carpeta grande lo subido puede quedar más abajo de lo cargado:
    // el aviso confirma que llegó aunque todavía no se vea.
    if (subidos > 0) {
      setAviso(
        lista.length === 1 ? `Se subió "${lista[0].name}".` : `Se subieron ${subidos} ${subidos === lista.length ? '' : `de ${lista.length} `}archivos.`,
      );
    }
  }

  // Soltar archivos en cualquier parte de la ventana los sube a la carpeta
  // abierta: con pocos archivos, la lista ocupaba poco y el área era chica.
  // Los arrastres internos (mover a una carpeta) no traen "Files" y se ignoran.
  const subirRef = useRef(subir);
  subirRef.current = subir;
  const moviendoRef = useRef(moviendo);
  moviendoRef.current = moviendo;

  useEffect(() => {
    let profundidad = 0;
    const traeArchivos = (e: DragEvent) =>
      !moviendoRef.current && (e.dataTransfer?.types.includes('Files') ?? false);

    const entrar = (e: DragEvent) => {
      if (!traeArchivos(e)) return;
      profundidad++;
      setArrastrandoArchivos(true);
    };
    const salir = (e: DragEvent) => {
      if (!traeArchivos(e)) return;
      profundidad = Math.max(0, profundidad - 1);
      if (profundidad === 0) setArrastrandoArchivos(false);
    };
    const sobre = (e: DragEvent) => {
      if (!traeArchivos(e)) return;
      // Sin esto el navegador abre el archivo en vez de dejarlo soltar.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const soltar = (e: DragEvent) => {
      if (!traeArchivos(e)) return;
      e.preventDefault();
      profundidad = 0;
      setArrastrandoArchivos(false);
      if (e.dataTransfer?.files.length) void subirRef.current(e.dataTransfer.files);
    };

    window.addEventListener('dragenter', entrar);
    window.addEventListener('dragleave', salir);
    window.addEventListener('dragover', sobre);
    window.addEventListener('drop', soltar);
    return () => {
      window.removeEventListener('dragenter', entrar);
      window.removeEventListener('dragleave', salir);
      window.removeEventListener('dragover', sobre);
      window.removeEventListener('drop', soltar);
    };
  }, []);

  async function crearCarpeta(nombre: string) {
    setDialogo(null);
    try {
      await api.createFolder(path, nombre);
      await cargar(path);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos crear la carpeta.');
    }
  }

  async function borrar(lista: FileEntry[]) {
    setDialogo(null);
    const r = await enLote(lista, (e) => api.remove(e.path));
    quitarDeLaVista(r.ok.map((e) => e.path));

    if (lista.length === 1 && r.fallidos.length === 1) {
      setError(r.fallidos[0].motivo);
      return;
    }
    informar(r, lista.length, (c) =>
      lista.length === 1 ? `"${lista[0].name}" se envió a la papelera.` : `Se enviaron ${c} elementos a la papelera.`,
    );
  }

  async function descargar(entry: FileEntry) {
    try {
      const { url } = await api.downloadUrl(entry.path);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos generar la descarga.');
    }
  }

  /** Arrastrar un elemento seleccionado arrastra toda la selección. */
  function empezarArrastre(entry: FileEntry) {
    setMoviendo(sel.seleccion.has(entry.path) && seleccionadas.length > 1 ? seleccionadas : [entry]);
  }

  /** Soltar sobre una carpeta mueve ahí lo arrastrado. */
  async function soltarEn(destino: FileEntry) {
    const origenes = (moviendo ?? []).filter((o) => o.path !== destino.path);
    setMoviendo(null);
    setSobreCarpeta(null);
    if (origenes.length === 0) return;

    const r = await enLote(origenes, (o) =>
      api.move(o.path, destino.path + o.name + (o.kind === 'folder' ? '/' : '')),
    );
    quitarDeLaVista(r.ok.map((e) => e.path));
    informar(r, origenes.length, (c) =>
      origenes.length === 1
        ? `"${origenes[0].name}" se movió a "${destino.name}".`
        : `Se movieron ${c} elementos a "${destino.name}".`,
    );
  }

  function abrirEnEditor(entry: FileEntry) {
    window.open(urlEditor({ path: entry.path }), '_blank', 'noopener');
  }

  function opcionesDe(entry: FileEntry) {
    const office = entry.kind === 'file' && abreEnEditor(entry.name);
    return [
      {
        etiqueta: 'Abrir en el editor',
        onSelect: () => abrirEnEditor(entry),
        oculto: !office,
      },
      {
        etiqueta: 'Historial de versiones…',
        onSelect: () => setDialogo({ tipo: 'historial', entry }),
        oculto: !office,
      },
      { etiqueta: 'Descargar', onSelect: () => descargar(entry), oculto: entry.kind === 'folder' },
      { etiqueta: 'Renombrar', onSelect: () => setDialogo({ tipo: 'renombrar', entry }) },
      { etiqueta: 'Mover a…', onSelect: () => setDialogo({ tipo: 'mover', entries: [entry] }) },
      { etiqueta: 'Compartir…', onSelect: () => setDialogo({ tipo: 'compartir', entries: [entry] }) },
      {
        etiqueta: 'Crear link público…',
        onSelect: () => setDialogo({ tipo: 'link', entry }),
        oculto: entry.kind === 'folder',
      },
      {
        etiqueta: 'Borrar',
        onSelect: () => setDialogo({ tipo: 'borrar', entries: [entry] }),
        peligro: true,
      },
    ];
  }

  const arrastrable = (entry: FileEntry) => ({
    draggable: true,
    onDragStart: () => empezarArrastre(entry),
    onDragEnd: () => {
      setMoviendo(null);
      setSobreCarpeta(null);
    },
    onDragOver: (e: React.DragEvent) => {
      if (moviendo && entry.kind === 'folder' && !moviendo.some((m) => m.path === entry.path)) {
        e.preventDefault();
        setSobreCarpeta(entry.path);
      }
    },
    onDragLeave: () => setSobreCarpeta((actual) => (actual === entry.path ? null : actual)),
    onDrop: (e: React.DragEvent) => {
      if (!moviendo || entry.kind !== 'folder') return;
      e.preventDefault();
      void soltarEn(entry);
    },
  });

  const nombreCarpeta = path ? path.replace(/\/$/, '').split('/').pop() : 'Mis archivos';
  const quedanSinCargar = siguiente !== null && !busqueda.trim();

  return (
    <div className="zona">
      <div className="barra-herramientas">
        <Breadcrumb path={path} onNavigate={onPathChange} />

        <div className="herramientas-derecha">
          <div className="buscador">
            <label htmlFor="buscar" className="visualmente-oculto">
              Buscar en esta carpeta
            </label>
            <input
              id="buscar"
              type="search"
              placeholder="Buscar en esta carpeta…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>

          <div className="selector-vista" role="group" aria-label="Tipo de vista">
            <button
              className={vista === 'lista' ? 'activo' : ''}
              onClick={() => setVista('lista')}
              aria-pressed={vista === 'lista'}
              title="Ver como lista"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path d="M2 3h12v2H2V3Zm0 4h12v2H2V7Zm0 4h12v2H2v-2Z" />
              </svg>
            </button>
            <button
              className={vista === 'mosaico' ? 'activo' : ''}
              onClick={() => setVista('mosaico')}
              aria-pressed={vista === 'mosaico'}
              title="Ver como mosaico"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path d="M2 2h5v5H2V2Zm7 0h5v5H9V2ZM2 9h5v5H2V9Zm7 0h5v5H9V9Z" />
              </svg>
            </button>
          </div>

          <button onClick={() => inputArchivo.current?.click()}>Subir</button>
          <button className="secundario" onClick={() => setDialogo({ tipo: 'nueva-carpeta' })}>
            Nueva carpeta
          </button>
          <input
            id="selector-archivos"
            ref={inputArchivo}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) void subir(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {aviso && (
        <p className="aviso-ok">
          {aviso}
          <button className="cerrar-aviso" onClick={() => setAviso(null)} aria-label="Cerrar">
            ×
          </button>
        </p>
      )}

      {subidas.length > 0 && (
        <ul className="subidas">
          {subidas.map((s) => (
            <li key={s.nombre}>
              <span className="subida-nombre">{s.nombre}</span>
              {s.error ? (
                <span className="error">{s.error}</span>
              ) : (
                <span className="progreso">
                  <span className="progreso-relleno" style={{ width: `${s.progreso}%` }} />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <BarraSeleccion
        cantidad={seleccionadas.length}
        onLimpiar={sel.limpiar}
        nota={quedanSinCargar && sel.estado === 'todos' ? 'solo lo cargado; bajá para ver más' : null}
      >
        <button className="secundario" onClick={() => setDialogo({ tipo: 'mover', entries: seleccionadas })}>
          Mover…
        </button>
        <button
          className="secundario"
          onClick={() => setDialogo({ tipo: 'compartir', entries: seleccionadas })}
        >
          Compartir…
        </button>
        <button
          className="peligro-solido"
          onClick={() => setDialogo({ tipo: 'borrar', entries: seleccionadas })}
        >
          Borrar
        </button>
      </BarraSeleccion>

      {cargando && <p className="vacio">Cargando…</p>}

      {!cargando && entradas.length === 0 && !cargandoMas && (
        <p className="vacio vacio-grande">
          {busqueda
            ? `No hay nada que coincida con "${busqueda}".`
            : 'Esta carpeta está vacía. Arrastrá archivos a cualquier parte de la ventana para subirlos.'}
        </p>
      )}

      {!cargando && entradas.length > 0 && vista === 'lista' && (
        <table className="tabla">
          <thead>
            <tr>
              <th className="col-check">
                <CasillaTodos estado={sel.estado} onChange={sel.alternarTodos} etiqueta="Seleccionar todo" />
              </th>
              <th>Nombre</th>
              <th className="col-tamanio">Tamaño</th>
              <th className="col-fecha">Modificado</th>
              <th className="col-acciones" />
            </tr>
          </thead>
          <tbody>
            {entradas.map((entry) => {
              const marcada = sel.seleccion.has(entry.path);
              const clases = [
                sobreCarpeta === entry.path ? 'destino-activo' : '',
                marcada ? 'seleccionada' : '',
              ].join(' ');
              return (
                <tr key={entry.path} {...arrastrable(entry)} className={clases.trim()}>
                  <td className="col-check">
                    <Casilla
                      marcada={marcada}
                      onCambio={(shift) => sel.alternar(entry.path, shift)}
                      etiqueta={`Seleccionar ${entry.name}`}
                    />
                  </td>
                  <td>
                    {entry.kind === 'folder' ? (
                      <button className="enlace-carpeta" onClick={() => onPathChange(entry.path)}>
                        <IconoDe nombre={entry.name} esCarpeta />
                        {entry.name}
                      </button>
                    ) : abreEnEditor(entry.name) ? (
                      <button className="enlace-carpeta" onClick={() => abrirEnEditor(entry)}>
                        <IconoDe nombre={entry.name} esCarpeta={false} />
                        {entry.name}
                      </button>
                    ) : (
                      <span className="nombre-archivo">
                        <IconoDe nombre={entry.name} esCarpeta={false} />
                        {entry.name}
                      </span>
                    )}
                  </td>
                  <td className="col-tamanio">
                    {entry.kind === 'file' ? formatBytes(entry.sizeBytes) : ''}
                  </td>
                  <td className="col-fecha">{formatDate(entry.modifiedAt)}</td>
                  <td className="col-acciones">
                    <MenuAcciones opciones={opcionesDe(entry)} etiqueta={`Acciones de ${entry.name}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!cargando && entradas.length > 0 && vista === 'mosaico' && (
        <>
          <label className="seleccionar-todo">
            <CasillaTodos estado={sel.estado} onChange={sel.alternarTodos} etiqueta="Seleccionar todo" />
            Seleccionar todo
          </label>
          <ul className={seleccionadas.length > 0 ? 'mosaico con-seleccion' : 'mosaico'}>
            {entradas.map((entry) => {
              const marcada = sel.seleccion.has(entry.path);
              const clases = ['tarjeta', sobreCarpeta === entry.path ? 'destino-activo' : '', marcada ? 'seleccionada' : ''];
              return (
                <li key={entry.path} {...arrastrable(entry)} className={clases.join(' ').trim()}>
                  <div className="tarjeta-check">
                    <Casilla
                      marcada={marcada}
                      onCambio={(shift) => sel.alternar(entry.path, shift)}
                      etiqueta={`Seleccionar ${entry.name}`}
                    />
                  </div>
                  <div className="tarjeta-menu">
                    <MenuAcciones opciones={opcionesDe(entry)} etiqueta={`Acciones de ${entry.name}`} />
                  </div>

                  <button
                    className="tarjeta-cuerpo"
                    onClick={() =>
                      entry.kind === 'folder' ? onPathChange(entry.path) : abrirEnEditor(entry)
                    }
                    disabled={entry.kind === 'file' && !abreEnEditor(entry.name)}
                  >
                    <IconoDe nombre={entry.name} esCarpeta={entry.kind === 'folder'} />
                    <span className="tarjeta-nombre">{entry.name}</span>
                    <span className="tarjeta-meta">
                      {entry.kind === 'file' ? formatBytes(entry.sizeBytes) : 'Carpeta'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* Marca el final de lo cargado: al acercarse a la pantalla pide más. */}
      {!cargando && siguiente && (
        <div ref={centinela} className="centinela">
          {cargandoMas && (busqueda.trim() ? 'Buscando en toda la carpeta…' : 'Cargando más…')}
        </div>
      )}

      {arrastrandoArchivos && (
        <div className="soltar-overlay" aria-hidden="true">
          <div className="soltar-caja">
            <svg viewBox="0 0 24 24">
              <path d="M11 16V7.8l-3.3 3.3-1.4-1.4L12 4l5.7 5.7-1.4 1.4L13 7.8V16h-2Zm-6 4v-5h2v3h10v-3h2v5H5Z" />
            </svg>
            <span>
              Soltá para subir a <strong>{nombreCarpeta}</strong>
            </span>
          </div>
        </div>
      )}

      {dialogo?.tipo === 'renombrar' && (
        <RenombrarDialog
          entry={dialogo.entry}
          carpeta={path}
          onClose={() => setDialogo(null)}
          onListo={() => {
            setDialogo(null);
            void cargar(path);
          }}
        />
      )}

      {dialogo?.tipo === 'mover' && (
        <MoverDialog
          entries={dialogo.entries}
          carpetas={carpetas}
          onClose={() => setDialogo(null)}
          onListo={(r) => {
            const total = dialogo.entries.length;
            setDialogo(null);
            quitarDeLaVista(r.ok.map((e) => e.path));
            informar(r, total, (c) =>
              total === 1 ? `"${dialogo.entries[0].name}" se movió.` : `Se movieron ${c} elementos.`,
            );
          }}
        />
      )}

      {dialogo?.tipo === 'compartir' && (
        <CompartirDialog
          paths={dialogo.entries.map((e) => e.path)}
          nombre={dialogo.entries.length === 1 ? dialogo.entries[0].name : `${dialogo.entries.length} elementos`}
          onClose={() => setDialogo(null)}
          onListo={(mensaje) => {
            setDialogo(null);
            setAviso(mensaje);
          }}
        />
      )}

      {dialogo?.tipo === 'historial' && (
        <HistorialDialog
          ubicacion={{ path: dialogo.entry.path }}
          nombre={dialogo.entry.name}
          onClose={() => {
            setDialogo(null);
            void cargar(path);
          }}
        />
      )}

      {dialogo?.tipo === 'nueva-carpeta' && (
        <Pedir
          titulo="Nueva carpeta"
          etiqueta="Nombre"
          etiquetaAceptar="Crear"
          onCancelar={() => setDialogo(null)}
          onAceptar={crearCarpeta}
        />
      )}

      {dialogo?.tipo === 'borrar' && (
        <Confirmar
          titulo={tituloBorrar(dialogo.entries)}
          mensaje={`${
            dialogo.entries.length > 1
              ? `${dialogo.entries.length} elementos van a la papelera${dialogo.entries.some((e) => e.kind === 'folder') ? ', las carpetas con todo su contenido' : ''}.`
              : dialogo.entries[0].kind === 'folder'
                ? `"${dialogo.entries[0].name}" va a la papelera con todo su contenido.`
                : `"${dialogo.entries[0].name}" va a la papelera.`
          } Se puede restaurar desde ahí. Si estaba compartido o tenía links públicos, dejan de funcionar.`}
          etiquetaAceptar="Borrar"
          peligro
          onCancelar={() => setDialogo(null)}
          onAceptar={() => borrar(dialogo.entries)}
        />
      )}

      {dialogo?.tipo === 'link' && (
        <LinkDialog
          path={dialogo.entry.path}
          nombre={dialogo.entry.name}
          onClose={() => setDialogo(null)}
          onListo={(url) => {
            setDialogo(null);
            setAviso(`Link creado: ${url}`);
          }}
        />
      )}
    </div>
  );
}

function tituloBorrar(entries: FileEntry[]): string {
  if (entries.length > 1) return `Borrar ${entries.length} elementos`;
  return entries[0].kind === 'folder' ? 'Borrar carpeta' : 'Borrar archivo';
}

function Breadcrumb({ path, onNavigate }: { path: string; onNavigate: (p: string) => void }) {
  const partes = path.split('/').filter(Boolean);

  return (
    <nav className="breadcrumb" aria-label="Ubicación">
      <button className="link" onClick={() => onNavigate('')}>
        Mis archivos
      </button>
      {partes.map((parte, i) => {
        const destino = partes.slice(0, i + 1).join('/') + '/';
        const ultimo = i === partes.length - 1;
        return (
          <span key={destino}>
            <span className="separador">/</span>
            {ultimo ? (
              <span className="actual">{parte}</span>
            ) : (
              <button className="link" onClick={() => onNavigate(destino)}>
                {parte}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}
